import type { SkillCatalog, SkillFunction, SkillJob, SkillProposal, SkillCapability } from "../hooks/useSkills";

/** Same identity rule as backend models::page_key, before SHA256. Data inputs
 * may vary on a known application; unknown query parameters remain significant. */
export function pageIdentity(source?:string):string {
  try {
    const url=new URL(source??"");
    if(url.hostname==="app.ahrefs.com") {
      const pairs=[...url.searchParams].filter(([key])=>!["target","domain","projectId","project_id","page","offset","cursor"].includes(key));
      url.search=""; for(const [key,value] of pairs)url.searchParams.append(key,value);
    }
    return url.href;
  }catch{return "unknown";}
}
// Display the page path; the exact, possibly very long URL remains in the
// scope disclosure and in every request. Display labels never define identity.
export const pathOf=(url?:string)=>{try{return new URL(url??"").pathname;}catch{return "Page d’origine non renseignée";}};
export type CatalogSkill={key:string;title:string;source?:string;pageTitle?:string;fn?:SkillFunction;proposal?:SkillProposal};
export type CatalogPage={id:string;title:string;url?:string;skills:CatalogSkill[];capabilities?:SkillCapability[]};
export const excludedReason=(skill:CatalogSkill)=>skill.fn?.quality_reason??(!skill.fn?(skill.proposal?.quality_reason??(skill.proposal?.review_group==="legacy_navigation"?"navigation_only":undefined)):undefined);
export const qualityLabel=(reason:string)=>({navigation_only:"Simple navigation : aucun résultat métier capturé",unstructured_page_dump:"Texte de page complet sans extraction précise",duplicate_behavior:"Fonction déjà proposée ou disponible",duplicate_output:"Plusieurs champs utilisent la même extraction : résultat ambigu"} as Record<string,string>)[reason]??reason;

export function catalogPages(catalog:SkillCatalog|null|undefined):CatalogPage[] {
  if(!catalog)return [];
  const pages=new Map<string,CatalogPage>();
  const ensure=(url?:string,title?:string)=>{
    const id=pageIdentity(url), existing=pages.get(id);
    if(existing){if(title)existing.title=title;return existing;}
    const page:CatalogPage={id,title:title||pathOf(url),url,skills:[],capabilities:[]};pages.set(id,page);return page;
  };
  for(const page of catalog.pages)ensure(page.source_url,page.title);
  const entries=new Map<string,CatalogSkill>();
  for(const fn of catalog.functions)entries.set(fn.key,{key:fn.key,title:fn.specification.title,source:fn.source_url,pageTitle:fn.page_title,fn});
  for(const p of catalog.proposals){
    const key=p.function_key??p.specification.key??p.id, existing=entries.get(key);
    // Available review rows can be hidden aliases of a canonical active skill.
    if(p.status==="available"&&!existing)continue;
    if(existing){existing.proposal=p;if(!existing.source)existing.source=p.source_url;}
    else entries.set(key,{key,title:p.specification.title,source:p.source_url,pageTitle:p.page_title,proposal:p});
  }
  for(const entry of entries.values())ensure(entry.source,entry.pageTitle).skills.push(entry);
  for(const idea of catalog.capabilities??[]) {
    if(idea.status==="exploration_step"||entries.has(idea.key))continue;
    ensure(idea.source_url,idea.source_title).capabilities!.push(idea);
  }
  return [...pages.values()].sort((a,b)=>a.title.localeCompare(b.title,"fr")).map(page=>({...page,skills:page.skills.sort((a,b)=>a.title.localeCompare(b.title,"fr"))}));
}

export function jobBelongsToSkill(job:SkillJob,item:CatalogSkill):boolean {
  return job.request.function_key===item.key || !!job.request.proposals?.some(p=>p.function_key===item.key||p.specification?.key===item.key||p.id===item.proposal?.id)
    || !!item.proposal&&!!job.progress.proposals?.includes(item.proposal.id);
}
